import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, At, CheckCircle, DownloadSimple, EnvelopeSimple, Globe,
  MagnifyingGlass, Plus, UploadSimple, Warning, X,
} from '@phosphor-icons/react';
import SearchableSelect from '../../components/SearchableSelect';
import { ModuleShell } from '../../components/module-ui';
import { apiPath } from '../../config/apiBase';
import { notifyError, notifySuccess } from '../../lib/notify';

const EMAIL_MAPPING = apiPath('/api/email-mapping');
const PAGE_SIZE = 25;

const STATUS_META = {
  new: { label: 'New', tone: 'ok' },
  update: { label: 'Update', tone: 'warn' },
  duplicate: { label: 'Already mapped', tone: 'muted' },
  unknown_client: { label: 'Unknown client', tone: 'bad' },
  invalid: { label: 'Invalid', tone: 'bad' },
};

function authHeaders() {
  const token = localStorage.getItem('token');
  return { withCredentials: true, headers: { Authorization: `Bearer ${token}` } };
}

function errText(err, fallback) {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

function clientOptions(clients, extraValue) {
  const opts = (clients || []).map((c) => ({
    value: c.value,
    label: c.parent_value ? `[${c.parent_value}] ${c.label || c.value}` : (c.label || c.value),
    searchText: `${c.parent_value || ''} ${c.label || ''} ${c.value || ''}`,
  }));
  if (extraValue && !opts.some((o) => o.value === extraValue)) {
    opts.unshift({ value: extraValue, label: `${extraValue} (not on product)`, searchText: extraValue });
  }
  return opts;
}

/** Branches for a company from the product catalog (case-insensitive key). */
function branchesForClient(branchesByClient, clientName) {
  if (!clientName || !branchesByClient) return [];
  if (branchesByClient[clientName]) return branchesByClient[clientName];
  const target = String(clientName).trim().toLowerCase();
  const key = Object.keys(branchesByClient).find((k) => String(k).trim().toLowerCase() === target);
  return key ? (branchesByClient[key] || []) : [];
}

function branchOptions(branchesByClient, clientName, extraValue) {
  const opts = branchesForClient(branchesByClient, clientName).map((b) => ({
    value: b.value,
    label: b.label || b.value,
    searchText: `${b.label || ''} ${b.value || ''}`,
  }));
  // Always allow clearing to company-level only
  opts.unshift({ value: '', label: '— No branch (company only) —', searchText: 'none company only' });
  if (extraValue && !opts.some((o) => o.value === extraValue)) {
    opts.splice(1, 0, {
      value: extraValue,
      label: `${extraValue} (not in catalog)`,
      searchText: extraValue,
    });
  }
  return opts;
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function formatWhen(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function EmailClientMappingPage() {
  const { emailId } = useParams();
  const navigate = useNavigate();

  const [mailbox, setMailbox] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [clients, setClients] = useState([]);
  const [branchesByClient, setBranchesByClient] = useState({});
  const [hasBranchCatalog, setHasBranchCatalog] = useState(false);
  const [tab, setTab] = useState('all'); // all | by-client | unmapped

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingRows, setLoadingRows] = useState(true);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [matchTypeFilter, setMatchTypeFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');

  const [groups, setGroups] = useState([]);
  const [groupSummary, setGroupSummary] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [railSearch, setRailSearch] = useState('');
  const [showUnmappedOnly, setShowUnmappedOnly] = useState(false);

  const [senders, setSenders] = useState([]);
  const [loadingSenders, setLoadingSenders] = useState(false);
  const [senderDraft, setSenderDraft] = useState({}); // sender → { client_name, match_type, backfill }
  const [savingSender, setSavingSender] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawer, setDrawer] = useState({
    match_type: 'address', values: '', client_name: '', branch: '', contact_name: '',
  });
  const [savingDrawer, setSavingDrawer] = useState(false);
  const [drawerResult, setDrawerResult] = useState(null);

  const [csvOpen, setCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvReport, setCsvReport] = useState(null);
  const [csvResult, setCsvResult] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvHideClean, setCsvHideClean] = useState(false);
  const [csvDefaultClient, setCsvDefaultClient] = useState('');
  const csvInputRef = useRef(null);

  const [editingRoute, setEditingRoute] = useState(null);
  const [editClient, setEditClient] = useState('');
  const [editBranch, setEditBranch] = useState('');

  const base = `${EMAIL_MAPPING}/${encodeURIComponent(emailId)}`;

  const loadMailbox = useCallback(async () => {
    try {
      const r = await axios.get(EMAIL_MAPPING, authHeaders());
      const row = (r.data || []).find((e) => e.email_id === emailId);
      if (!row) {
        setLoadError('This mailbox is not available to you.');
        return null;
      }
      setMailbox(row);
      return row;
    } catch (err) {
      setLoadError(errText(err, 'Failed to load mailbox'));
      return null;
    }
  }, [emailId]);

  const loadClients = useCallback(async (productId) => {
    if (!productId) return;
    try {
      const r = await axios.get(
        `${EMAIL_MAPPING}/products/${encodeURIComponent(productId)}/clients`,
        authHeaders(),
      );
      setClients(r.data?.clients || []);
    } catch {
      setClients([]);
    }
  }, []);

  const loadBranches = useCallback(async (productId) => {
    if (!productId) {
      setBranchesByClient({});
      setHasBranchCatalog(false);
      return;
    }
    try {
      const r = await axios.get(
        `${EMAIL_MAPPING}/products/${encodeURIComponent(productId)}/branches`,
        authHeaders(),
      );
      setBranchesByClient(r.data?.branches_by_client || {});
      setHasBranchCatalog(Boolean(r.data?.has_catalog));
    } catch {
      setBranchesByClient({});
      setHasBranchCatalog(false);
    }
  }, []);

  const loadRows = useCallback(async () => {
    setLoadingRows(true);
    try {
      const r = await axios.get(`${base}/routes`, {
        ...authHeaders(),
        params: {
          search: appliedSearch || undefined,
          match_type: matchTypeFilter || undefined,
          client: clientFilter || undefined,
          page,
          page_size: PAGE_SIZE,
        },
      });
      setRows(r.data?.rows || []);
      setTotal(r.data?.total || 0);
    } catch (err) {
      setRows([]);
      setTotal(0);
      setLoadError(errText(err, 'Failed to load mappings'));
    } finally {
      setLoadingRows(false);
    }
  }, [base, appliedSearch, matchTypeFilter, clientFilter, page]);

  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      const r = await axios.get(`${base}/routes/by-client`, authHeaders());
      setGroups(r.data?.groups || []);
      setGroupSummary({
        total_routes: r.data?.total_routes || 0,
        clients_mapped: r.data?.clients_mapped || 0,
        clients_total: r.data?.clients_total || 0,
      });
    } catch {
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  }, [base]);

  const loadSenders = useCallback(async () => {
    setLoadingSenders(true);
    try {
      const r = await axios.get(`${base}/unmapped-senders`, authHeaders());
      setSenders(r.data?.senders || []);
    } catch {
      setSenders([]);
    } finally {
      setLoadingSenders(false);
    }
  }, [base]);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadMailbox(), loadRows(), loadGroups(), loadSenders()]);
  }, [loadMailbox, loadRows, loadGroups, loadSenders]);

  useEffect(() => {
    (async () => {
      const row = await loadMailbox();
      if (row?.product_id) {
        await Promise.all([loadClients(row.product_id), loadBranches(row.product_id)]);
      }
      await Promise.all([loadGroups(), loadSenders()]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId]);

  useEffect(() => { loadRows(); }, [loadRows]);

  // Debounce the search box so typing doesn't hammer the API.
  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const unmappedSenderCount = senders.length;
  const clientCount = groups.length || clients.length;
  const gapCount = useMemo(() => groups.filter((g) => !g.route_count).length, [groups]);

  const railGroups = useMemo(() => {
    const q = railSearch.trim().toLowerCase();
    let list = groups;
    if (showUnmappedOnly) list = list.filter((g) => g.route_count === 0);
    if (q) {
      list = list.filter((g) => (
        `${g.client_label || ''} ${g.client_name || ''} ${g.parent_value || ''}`.toLowerCase().includes(q)
      ));
    }
    return list;
  }, [groups, railSearch, showUnmappedOnly]);

  const railByParent = useMemo(() => {
    const buckets = new Map();
    railGroups.forEach((g) => {
      const key = g.parent_value || 'Other';
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(g);
    });
    return [...buckets.entries()];
  }, [railGroups]);

  const openDrawer = (preset = {}) => {
    setDrawer({
      match_type: preset.match_type || 'address',
      values: preset.values || '',
      client_name: preset.client_name || clientFilter || '',
      branch: preset.branch || '',
      contact_name: '',
    });
    setDrawerResult(null);
    setDrawerOpen(true);
  };

  const submitDrawer = async () => {
    const values = drawer.values.trim();
    if (!values) {
      notifyError(drawer.match_type === 'address' ? 'Enter at least one email address' : 'Enter at least one domain');
      return;
    }
    if (!drawer.client_name) {
      notifyError('Select the company / client these senders belong to');
      return;
    }
    setSavingDrawer(true);
    try {
      const r = await axios.post(`${base}/routes`, {
        match_type: drawer.match_type,
        match_values: values,
        client_name: drawer.client_name,
        branch: drawer.branch?.trim() || null,
        contact_name: drawer.contact_name || null,
      }, authHeaders());
      setDrawerResult(r.data);
      // Keep type + client so the next paste for the same client is one step.
      setDrawer((d) => ({ ...d, values: '', contact_name: '' }));
      const { created = 0, already_mapped = [], conflicts = [], invalid = [] } = r.data || {};
      const where = r.data.branch ? `${r.data.client_name} / ${r.data.branch}` : r.data.client_name;
      if (created) notifySuccess(`Added ${created} sender${created === 1 ? '' : 's'} to ${where}`);
      else if (!conflicts.length && !invalid.length && already_mapped.length) notifySuccess('Already mapped — nothing to change');
      await Promise.all([loadRows(), loadGroups(), loadSenders(), loadMailbox()]);
    } catch (err) {
      notifyError(errText(err, 'Failed to add mapping'));
    } finally {
      setSavingDrawer(false);
    }
  };

  const removeRoute = async (route) => {
    const where = route.branch ? `${route.client_name} / ${route.branch}` : route.client_name;
    if (!window.confirm(`Remove ${route.match_value} → ${where}?`)) return;
    try {
      await axios.delete(`${base}/routes/${encodeURIComponent(route.route_id)}`, authHeaders());
      notifySuccess('Mapping removed');
      await Promise.all([loadRows(), loadGroups(), loadSenders(), loadMailbox()]);
    } catch (err) {
      notifyError(errText(err, 'Failed to remove mapping'));
    }
  };

  const saveRouteClient = async () => {
    if (!editingRoute || !editClient) return;
    try {
      await axios.patch(
        `${base}/routes/${encodeURIComponent(editingRoute.route_id)}`,
        { client_name: editClient, branch: editBranch.trim() || null },
        authHeaders(),
      );
      const where = editBranch.trim() ? `${editClient} / ${editBranch.trim()}` : editClient;
      notifySuccess(`${editingRoute.match_value} → ${where}`);
      setEditingRoute(null);
      setEditClient('');
      setEditBranch('');
      await Promise.all([loadRows(), loadGroups(), loadMailbox()]);
    } catch (err) {
      notifyError(errText(err, 'Failed to move mapping'));
    }
  };

  const unmapClient = async (clientName, count) => {
    if (!window.confirm(`Remove all ${count} sender mapping(s) for ${clientName}?`)) return;
    try {
      await axios.post(`${base}/routes/bulk-delete`, { client_name: clientName }, authHeaders());
      notifySuccess(`Removed ${count} mapping(s)`);
      await Promise.all([loadRows(), loadGroups(), loadSenders(), loadMailbox()]);
    } catch (err) {
      notifyError(errText(err, 'Failed to remove mappings'));
    }
  };

  const mapSender = async (row) => {
    const draft = senderDraft[row.sender_address] || {};
    if (!draft.client_name) {
      notifyError('Select a company / client first');
      return;
    }
    setSavingSender(row.sender_address);
    try {
      const r = await axios.post(`${base}/unmapped-senders/map`, {
        sender_address: row.sender_address,
        client_name: draft.client_name,
        branch: draft.branch?.trim() || null,
        match_type: draft.match_type || 'address',
        backfill: draft.backfill !== false,
      }, authHeaders());
      const n = r.data?.backfilled_tickets || 0;
      const where = r.data.branch ? `${r.data.client_name} / ${r.data.branch}` : r.data.client_name;
      notifySuccess(
        `${r.data.match_value} → ${where}${n ? ` · ${n} open ticket(s) updated` : ''}`,
      );
      setSenderDraft((prev) => {
        const next = { ...prev };
        delete next[row.sender_address];
        return next;
      });
      await refreshAll();
    } catch (err) {
      notifyError(errText(err, 'Failed to map sender'));
    } finally {
      setSavingSender(null);
    }
  };

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${base}/routes/export`, { ...authHeaders(), responseType: 'blob' });
      downloadBlob(r.data, `${(mailbox?.email_address || 'mailbox').replace(/[^a-z0-9]+/gi, '-')}-client-routes.csv`);
    } catch (err) {
      notifyError(errText(err, 'Export failed'));
    }
  };

  const downloadTemplate = async (onlyGaps) => {
    setCsvBusy(true);
    try {
      const r = await axios.get(`${base}/routes/template`, {
        ...authHeaders(),
        params: onlyGaps ? { only_gaps: true } : undefined,
        responseType: 'blob',
      });
      const slug = (mailbox?.email_address || 'mailbox').replace(/[^a-z0-9]+/gi, '-');
      downloadBlob(r.data, `${slug}-${onlyGaps ? 'unmapped-clients' : 'clients'}.csv`);
    } catch (err) {
      notifyError(errText(err, 'Could not build the client sheet'));
    } finally {
      setCsvBusy(false);
    }
  };

  const validateCsv = async (file) => {
    if (!file) return;
    setCsvBusy(true);
    setCsvResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await axios.post(`${base}/routes/validate`, form, {
        ...authHeaders(),
        params: csvDefaultClient ? { default_client: csvDefaultClient } : undefined,
      });
      setCsvReport(r.data);
      setCsvFile(file);
    } catch (err) {
      setCsvReport(null);
      notifyError(errText(err, 'Could not read that CSV'));
    } finally {
      setCsvBusy(false);
      if (csvInputRef.current) csvInputRef.current.value = '';
    }
  };

  const commitCsv = async () => {
    if (!csvFile) return;
    setCsvBusy(true);
    try {
      const form = new FormData();
      form.append('file', csvFile);
      const r = await axios.post(`${base}/routes/import`, form, {
        ...authHeaders(),
        params: csvDefaultClient ? { default_client: csvDefaultClient } : undefined,
      });
      setCsvResult(r.data);
      notifySuccess(`Imported ${r.data.created} new, ${r.data.updated} updated`);
      await refreshAll();
    } catch (err) {
      notifyError(errText(err, 'Import failed'));
    } finally {
      setCsvBusy(false);
    }
  };

  const downloadRejected = () => {
    const rowsOut = csvResult?.rejected_rows || csvReport?.rows?.filter(
      (x) => x.status === 'invalid' || x.status === 'unknown_client',
    ) || [];
    if (!rowsOut.length) return;
    const lines = ['email,client_name,branch,match_type,problem'];
    rowsOut.forEach((x) => {
      const cells = [
        x.input_value || '', x.input_client || '', x.branch || '', x.match_type || '', x.message || '',
      ].map((c) => `"${String(c).replace(/"/g, '""')}"`);
      lines.push(cells.join(','));
    });
    downloadBlob(new Blob([lines.join('\n')], { type: 'text/csv' }), 'rejected-rows.csv');
  };

  const closeCsv = () => {
    setCsvOpen(false);
    setCsvFile(null);
    setCsvReport(null);
    setCsvResult(null);
    setCsvHideClean(false);
    setCsvDefaultClient('');
  };

  if (loadError && !mailbox) {
    return (
      <ModuleShell className="ecm-module" title="Client email mapping" icon={EnvelopeSimple}>
        <div className="ecm-card ecm-empty">
          <Warning size={20} />
          <div>{loadError}</div>
          <button type="button" className="tk-btn" onClick={() => navigate('/dashboard/products')}>
            Back to Email mapping
          </button>
        </div>
      </ModuleShell>
    );
  }

  const isShared = (mailbox?.routing_mode || 'single') === 'shared';
  const clientSel = clientOptions(clients);

  return (
    <ModuleShell
      className="ecm-module"
      title={mailbox?.email_address || 'Client email mapping'}
      subtitle={
        isShared
          ? 'Shared inbox — each sender address or domain decides which client the ticket belongs to.'
          : 'This mailbox is in single-client mode. Switch it to shared inbox to route by sender.'
      }
      icon={EnvelopeSimple}
      headerActions={(
        <div className="ecm-actions">
          <button type="button" className="tk-btn" onClick={() => navigate('/dashboard/products')}>
            <ArrowLeft size={15} /> Email mapping
          </button>
          <button
            type="button"
            className="tk-btn"
            disabled={csvBusy}
            title="CSV of every client on this product, ready for you to type the sender addresses beside each one"
            onClick={() => downloadTemplate(false)}
          >
            <DownloadSimple size={15} /> Client sheet
          </button>
          <button type="button" className="tk-btn" onClick={exportCsv} title="CSV of the sender mappings that exist today">
            <DownloadSimple size={15} /> Export
          </button>
          <button type="button" className="tk-btn" onClick={() => setCsvOpen(true)}>
            <UploadSimple size={15} /> Import
          </button>
          <button type="button" className="tk-btn tk-btn-primary" onClick={() => openDrawer()}>
            <Plus size={15} weight="bold" /> Add mapping
          </button>
        </div>
      )}
    >
      <div className="ecm-stats">
        <div className="ecm-stat">
          <span className="ecm-stat-value">{mailbox?.route_count ?? groupSummary?.total_routes ?? 0}</span>
          <span className="ecm-stat-label">senders mapped</span>
        </div>
        <div className="ecm-stat">
          <span className="ecm-stat-value">
            {groupSummary ? `${groupSummary.clients_mapped}/${groupSummary.clients_total}` : '—'}
          </span>
          <span className="ecm-stat-label">clients covered</span>
        </div>
        <div className={`ecm-stat${unmappedSenderCount ? ' is-warn' : ''}`}>
          <span className="ecm-stat-value">{unmappedSenderCount}</span>
          <span className="ecm-stat-label">unmapped senders</span>
        </div>
        <div className="ecm-stat">
          <span className="ecm-stat-value">{mailbox?.product_name || '—'}</span>
          <span className="ecm-stat-label">product</span>
        </div>
      </div>

      {!isShared && (
        <div className="ecm-banner">
          <Warning size={16} />
          <span>
            Sender routing is only used when this mailbox is set to <strong>Shared inbox</strong> in Email
            mapping. Mappings added here are saved, but ignored until you switch the mode.
          </span>
        </div>
      )}

      <div className="ecm-layout">
        <aside className="ecm-rail">
          <div className="ecm-rail-head">
            <div className="ecm-rail-title">Clients</div>
            <label className="ecm-check">
              <input
                type="checkbox"
                checked={showUnmappedOnly}
                onChange={(e) => setShowUnmappedOnly(e.target.checked)}
              />
              Gaps only
            </label>
          </div>
          <div className="ecm-search ecm-search-sm">
            <MagnifyingGlass size={14} />
            <input
              value={railSearch}
              onChange={(e) => setRailSearch(e.target.value)}
              placeholder="Search clients…"
            />
          </div>
          <button
            type="button"
            className={`ecm-rail-item${!clientFilter ? ' is-active' : ''}`}
            onClick={() => { setClientFilter(''); setPage(1); }}
          >
            <span>All clients</span>
            <span className="ecm-count">{groupSummary?.total_routes ?? 0}</span>
          </button>
          <div className="ecm-rail-scroll">
            {loadingGroups ? (
              <div className="ecm-muted ecm-pad">Loading clients…</div>
            ) : railByParent.length === 0 ? (
              <div className="ecm-muted ecm-pad">No clients match.</div>
            ) : railByParent.map(([parent, items]) => (
              <div key={parent} className="ecm-rail-group">
                <div className="ecm-rail-group-label">{parent}</div>
                {items.map((g) => (
                  <button
                    key={g.client_name}
                    type="button"
                    className={`ecm-rail-item${clientFilter === g.client_name ? ' is-active' : ''}`}
                    onClick={() => {
                      setClientFilter(g.client_name);
                      setTab('all');
                      setPage(1);
                    }}
                    title={g.client_label}
                  >
                    <span className="ecm-rail-item-label">{g.client_label}</span>
                    <span className={`ecm-count${g.route_count === 0 ? ' is-zero' : ''}`}>{g.route_count}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </aside>

        <section className="ecm-main">
          <nav className="tk-subnav ecm-tabs" role="tablist" aria-label="Mapping views">
            {[
              { id: 'all', label: 'All mappings' },
              { id: 'by-client', label: 'By client' },
              { id: 'unmapped', label: 'Unmapped senders', badge: unmappedSenderCount },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                className={`tk-subnav-link${tab === t.id ? ' is-active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                <span>{t.label}</span>
                {t.badge ? <span className="ecm-badge">{t.badge}</span> : null}
              </button>
            ))}
          </nav>

          {tab === 'all' && (
            <div className="ecm-card">
              <div className="ecm-toolbar">
                <div className="ecm-search">
                  <MagnifyingGlass size={15} />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search address, domain, client or contact…"
                  />
                  {search ? (
                    <button type="button" className="ecm-icon-btn" onClick={() => setSearch('')} aria-label="Clear">
                      <X size={13} />
                    </button>
                  ) : null}
                </div>
                <select
                  className="ecm-select"
                  value={matchTypeFilter}
                  onChange={(e) => { setMatchTypeFilter(e.target.value); setPage(1); }}
                  aria-label="Match type"
                >
                  <option value="">All types</option>
                  <option value="address">Addresses</option>
                  <option value="domain">Domains</option>
                </select>
                {clientFilter ? (
                  <button type="button" className="ecm-chip" onClick={() => { setClientFilter(''); setPage(1); }}>
                    {clientFilter} <X size={12} />
                  </button>
                ) : null}
                <span className="ecm-muted ecm-right">{total} mapping{total === 1 ? '' : 's'}</span>
              </div>

              {loadingRows ? (
                <div className="ecm-muted ecm-pad">Loading mappings…</div>
              ) : rows.length === 0 ? (
                <div className="ecm-empty">
                  <At size={20} />
                  <div>
                    {appliedSearch || clientFilter || matchTypeFilter
                      ? 'No mappings match these filters.'
                      : 'No senders mapped yet. Add them one by one, or import your list as CSV.'}
                  </div>
                  <button type="button" className="tk-btn tk-btn-primary" onClick={() => openDrawer()}>
                    <Plus size={14} weight="bold" /> Add mapping
                  </button>
                </div>
              ) : (
                <>
                  <table className="ecm-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Sender</th>
                        <th>Company / Client</th>
                        <th>Branch</th>
                        <th>Contact</th>
                        <th>Added</th>
                        <th aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.route_id}>
                          <td>
                            <span className={`ecm-pill ecm-pill-${r.match_type}`}>
                              {r.match_type === 'domain' ? <Globe size={12} /> : <At size={12} />}
                              {r.match_type}
                            </span>
                          </td>
                          <td className="ecm-mono">{r.match_value}</td>
                          <td>
                            {editingRoute?.route_id === r.route_id ? (
                              <div className="ecm-inline-edit">
                                <div className="ecm-ss">
                                  <SearchableSelect
                                    options={clientOptions(clients, r.client_name)}
                                    value={editClient}
                                    onChange={(v) => {
                                      const next = v || '';
                                      setEditClient(next);
                                      const allowed = branchesForClient(branchesByClient, next)
                                        .map((b) => b.value);
                                      if (editBranch && !allowed.includes(editBranch)) {
                                        setEditBranch('');
                                      }
                                    }}
                                    placeholder="— Select company —"
                                    searchPlaceholder="Search companies…"
                                    aria-label="Move to company"
                                  />
                                </div>
                                <div className="ecm-ss" style={{ minWidth: 160 }}>
                                  <SearchableSelect
                                    options={branchOptions(branchesByClient, editClient, editBranch)}
                                    value={editBranch}
                                    onChange={(v) => setEditBranch(v || '')}
                                    placeholder={
                                      !editClient
                                        ? 'Select company first'
                                        : (hasBranchCatalog
                                          ? (branchesForClient(branchesByClient, editClient).length
                                            ? '— Branch (optional) —'
                                            : 'No branches for this company')
                                          : 'No branch catalog')
                                    }
                                    searchPlaceholder="Search branches…"
                                    aria-label="Branch"
                                    disabled={!editClient}
                                  />
                                </div>
                                <button type="button" className="tk-btn tk-btn-primary ecm-btn-sm" onClick={saveRouteClient}>
                                  Save
                                </button>
                                <button
                                  type="button"
                                  className="tk-btn ecm-btn-sm"
                                  onClick={() => { setEditingRoute(null); setEditClient(''); setEditBranch(''); }}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <>
                                <div>{r.client_name}</div>
                                {(r.client_group || r.client_type) && (
                                  <div className="ecm-sub">{r.client_group || r.client_type}</div>
                                )}
                              </>
                            )}
                          </td>
                          <td>{r.branch || <span className="ecm-muted">—</span>}</td>
                          <td>{r.contact_name || <span className="ecm-muted">—</span>}</td>
                          <td className="ecm-muted">{formatWhen(r.created_at)}</td>
                          <td className="ecm-row-actions">
                            <button
                              type="button"
                              className="ecm-link"
                              onClick={() => {
                                setEditingRoute(r);
                                setEditClient(r.client_name || '');
                                setEditBranch(r.branch || '');
                              }}
                            >
                              Edit
                            </button>
                            <button type="button" className="ecm-link is-danger" onClick={() => removeRoute(r)}>
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="ecm-pager">
                    <button
                      type="button"
                      className="tk-btn ecm-btn-sm"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </button>
                    <span className="ecm-muted">Page {page} of {pages}</span>
                    <button
                      type="button"
                      className="tk-btn ecm-btn-sm"
                      disabled={page >= pages}
                      onClick={() => setPage((p) => Math.min(pages, p + 1))}
                    >
                      Next
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {tab === 'by-client' && (
            <div className="ecm-card">
              {loadingGroups ? (
                <div className="ecm-muted ecm-pad">Loading…</div>
              ) : railGroups.length === 0 ? (
                <div className="ecm-muted ecm-pad">No clients match the rail filters.</div>
              ) : (
                <div className="ecm-groups">
                  {railGroups.map((g) => (
                    <details key={g.client_name} className="ecm-group" open={g.route_count > 0 && railGroups.length <= 12}>
                      <summary>
                        <span className="ecm-group-name">
                          {g.client_label}
                          {g.orphan ? <span className="ecm-tag is-warn">not on product</span> : null}
                        </span>
                        <span className="ecm-group-meta">
                          {g.parent_value ? <span className="ecm-sub">{g.parent_value}</span> : null}
                          <span className={`ecm-count${g.route_count === 0 ? ' is-zero' : ''}`}>
                            {g.route_count}
                          </span>
                        </span>
                      </summary>
                      <div className="ecm-group-body">
                        {g.routes.length === 0 ? (
                          <div className="ecm-muted">
                            No sender mapped — mail from this client will land without a client until you add one.
                          </div>
                        ) : (
                          <ul className="ecm-chips">
                            {g.routes.map((r) => (
                              <li key={r.route_id} className={`ecm-chip ecm-chip-${r.match_type}`}>
                                {r.match_type === 'domain' ? <Globe size={12} /> : <At size={12} />}
                                <span className="ecm-mono">{r.match_value}</span>
                                <button
                                  type="button"
                                  aria-label={`Remove ${r.match_value}`}
                                  onClick={() => removeRoute(r)}
                                >
                                  <X size={11} />
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="ecm-group-actions">
                          <button
                            type="button"
                            className="ecm-link"
                            onClick={() => openDrawer({ client_name: g.client_name })}
                          >
                            <Plus size={12} weight="bold" /> Add sender
                          </button>
                          {g.route_count > 0 && (
                            <button
                              type="button"
                              className="ecm-link is-danger"
                              onClick={() => unmapClient(g.client_name, g.route_count)}
                            >
                              Remove all
                            </button>
                          )}
                        </div>
                      </div>
                    </details>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'unmapped' && (
            <div className="ecm-card">
              <div className="ecm-toolbar">
                <span className="ecm-muted">
                  Senders that raised tickets with no client. Map one and its open tickets are updated too.
                </span>
                <button type="button" className="tk-btn ecm-btn-sm ecm-right" onClick={loadSenders}>
                  Refresh
                </button>
              </div>
              {loadingSenders ? (
                <div className="ecm-muted ecm-pad">Loading…</div>
              ) : senders.length === 0 ? (
                <div className="ecm-empty">
                  <CheckCircle size={20} />
                  <div>Every sender that has emailed this mailbox is mapped to a client.</div>
                </div>
              ) : (
                <table className="ecm-table">
                  <thead>
                    <tr>
                      <th>Sender</th>
                      <th>Tickets</th>
                      <th>Last seen</th>
                      <th>Map to company + branch</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {senders.map((s) => {
                      const draft = senderDraft[s.sender_address] || {};
                      const asDomain = draft.match_type === 'domain';
                      return (
                        <tr key={s.sender_address}>
                          <td>
                            <div className="ecm-mono">{s.sender_address}</div>
                            {s.sender_name ? <div className="ecm-sub">{s.sender_name}</div> : null}
                            {s.last_subject ? <div className="ecm-sub ecm-clip">{s.last_subject}</div> : null}
                          </td>
                          <td>
                            {s.ticket_count}
                            {s.last_ticket_no ? <div className="ecm-sub">{s.last_ticket_no}</div> : null}
                          </td>
                          <td className="ecm-muted">{formatWhen(s.last_seen)}</td>
                          <td>
                            <div className="ecm-ss">
                              <SearchableSelect
                                options={clientSel}
                                value={draft.client_name || ''}
                                onChange={(v) => {
                                  const next = v || '';
                                  const allowed = branchesForClient(branchesByClient, next)
                                    .map((b) => b.value);
                                  setSenderDraft((prev) => {
                                    const cur = prev[s.sender_address] || {};
                                    const keepBranch = cur.branch && allowed.includes(cur.branch)
                                      ? cur.branch
                                      : '';
                                    return {
                                      ...prev,
                                      [s.sender_address]: {
                                        ...cur,
                                        client_name: next,
                                        branch: keepBranch,
                                      },
                                    };
                                  });
                                }}
                                placeholder="— Select company —"
                                searchPlaceholder="Search companies…"
                                aria-label={`Company for ${s.sender_address}`}
                              />
                            </div>
                            <div className="ecm-ss" style={{ marginTop: 6 }}>
                              <SearchableSelect
                                options={branchOptions(
                                  branchesByClient,
                                  draft.client_name,
                                  draft.branch,
                                )}
                                value={draft.branch || ''}
                                onChange={(v) => setSenderDraft((prev) => ({
                                  ...prev,
                                  [s.sender_address]: {
                                    ...(prev[s.sender_address] || {}),
                                    branch: v || '',
                                  },
                                }))}
                                placeholder={
                                  !draft.client_name
                                    ? 'Select company first'
                                    : (hasBranchCatalog
                                      ? (branchesForClient(branchesByClient, draft.client_name).length
                                        ? '— Branch (optional) —'
                                        : 'No branches for this company')
                                      : 'No branch catalog')
                                }
                                searchPlaceholder="Search branches…"
                                aria-label={`Branch for ${s.sender_address}`}
                                disabled={!draft.client_name}
                              />
                            </div>
                            <div className="ecm-sender-opts">
                              <label className="ecm-check" title={s.is_public_domain ? 'Public provider — domain mapping is blocked' : ''}>
                                <input
                                  type="checkbox"
                                  disabled={s.is_public_domain}
                                  checked={asDomain}
                                  onChange={(e) => setSenderDraft((prev) => ({
                                    ...prev,
                                    [s.sender_address]: {
                                      ...(prev[s.sender_address] || {}),
                                      match_type: e.target.checked ? 'domain' : 'address',
                                    },
                                  }))}
                                />
                                Whole domain (@{s.domain})
                              </label>
                              <label className="ecm-check">
                                <input
                                  type="checkbox"
                                  checked={draft.backfill !== false}
                                  onChange={(e) => setSenderDraft((prev) => ({
                                    ...prev,
                                    [s.sender_address]: {
                                      ...(prev[s.sender_address] || {}),
                                      backfill: e.target.checked,
                                    },
                                  }))}
                                />
                                Update its open tickets
                              </label>
                            </div>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="tk-btn tk-btn-primary ecm-btn-sm"
                              disabled={savingSender === s.sender_address}
                              onClick={() => mapSender(s)}
                            >
                              {savingSender === s.sender_address ? 'Saving…' : 'Map'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </section>
      </div>

      {drawerOpen && (
        <>
          <div className="ecm-scrim" role="presentation" onClick={() => setDrawerOpen(false)} />
          <aside className="ecm-drawer" aria-label="Add sender mapping">
            <div className="ecm-drawer-head">
              <div>
                <div className="ecm-drawer-title">Add sender mapping</div>
                <div className="ecm-sub">{mailbox?.email_address}</div>
              </div>
              <button type="button" className="ecm-icon-btn" onClick={() => setDrawerOpen(false)} aria-label="Close">
                <X size={16} />
              </button>
            </div>

            <div className="ecm-drawer-body">
              <div className="ecm-field">
                <span className="ecm-label">Match on</span>
                <div className="ecm-segment">
                  {[
                    { id: 'address', label: 'Email address', icon: At },
                    { id: 'domain', label: 'Whole domain', icon: Globe },
                  ].map((opt) => {
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        className={drawer.match_type === opt.id ? 'is-active' : ''}
                        onClick={() => setDrawer((d) => ({ ...d, match_type: opt.id }))}
                      >
                        <Icon size={13} /> {opt.label}
                      </button>
                    );
                  })}
                </div>
                <span className="ecm-hint">
                  {drawer.match_type === 'domain'
                    ? 'Every sender at this domain maps to the client. Public providers like gmail.com are not allowed.'
                    : 'Only this exact address maps to the client. Address always wins over a domain rule.'}
                </span>
              </div>

              <div className="ecm-field">
                <span className="ecm-label">
                  {drawer.match_type === 'domain' ? 'Domains' : 'Email addresses'}
                </span>
                <textarea
                  className="ecm-textarea"
                  rows={5}
                  value={drawer.values}
                  onChange={(e) => setDrawer((d) => ({ ...d, values: e.target.value }))}
                  placeholder={drawer.match_type === 'domain'
                    ? 'acme.com\nacme.co.in'
                    : 'ravi@acme.com, accounts@acme.com\nbranch.hyd@acme.com'}
                />
                <span className="ecm-hint">Paste as many as you like — comma, space or one per line.</span>
              </div>

              <div className="ecm-field">
                <span className="ecm-label">Company / Client</span>
                <div className="ecm-ss">
                  <SearchableSelect
                    options={clientSel}
                    value={drawer.client_name}
                    onChange={(v) => {
                      const next = v || '';
                      const allowed = branchesForClient(branchesByClient, next).map((b) => b.value);
                      setDrawer((d) => ({
                        ...d,
                        client_name: next,
                        branch: d.branch && allowed.includes(d.branch) ? d.branch : '',
                      }));
                    }}
                    placeholder="— Select company —"
                    searchPlaceholder="Search companies…"
                    aria-label="Company"
                  />
                </div>
                <span className="ecm-hint">
                  {clients.length
                    ? `${clients.length} companies/clients on ${mailbox?.product_name || 'this product'}. Groups are shown in brackets.`
                    : 'This product has no client list configured.'}
                </span>
              </div>

              <div className="ecm-field">
                <span className="ecm-label">Branch <span className="ecm-muted">(optional)</span></span>
                <div className="ecm-ss">
                  <SearchableSelect
                    options={branchOptions(branchesByClient, drawer.client_name, drawer.branch)}
                    value={drawer.branch}
                    onChange={(v) => setDrawer((d) => ({ ...d, branch: v || '' }))}
                    placeholder={
                      !drawer.client_name
                        ? 'Select company first'
                        : (hasBranchCatalog
                          ? (branchesForClient(branchesByClient, drawer.client_name).length
                            ? '— Branch (optional) —'
                            : 'No branches listed for this company')
                          : 'No branch catalog on this product')
                    }
                    searchPlaceholder="Search branches…"
                    aria-label="Branch"
                    disabled={!drawer.client_name}
                  />
                </div>
                <span className="ecm-hint">
                  {hasBranchCatalog
                    ? 'Bound to this company\'s branches from the product catalog. Leave empty for company-only tickets.'
                    : 'This product has no branch catalog — branch stays optional/empty.'}
                </span>
              </div>

              <div className="ecm-field">
                <span className="ecm-label">Contact name <span className="ecm-muted">(optional)</span></span>
                <input
                  className="ecm-input"
                  value={drawer.contact_name}
                  onChange={(e) => setDrawer((d) => ({ ...d, contact_name: e.target.value }))}
                  placeholder="Who this address belongs to"
                />
              </div>

              {drawerResult && (
                <div className="ecm-result">
                  {drawerResult.created > 0 && (
                    <div className="ecm-result-line is-ok">
                      <CheckCircle size={14} /> Added {drawerResult.created} to {drawerResult.client_name}
                    </div>
                  )}
                  {(drawerResult.already_mapped || []).length > 0 && (
                    <div className="ecm-result-line">
                      {drawerResult.already_mapped.length} already mapped to this client
                    </div>
                  )}
                  {(drawerResult.conflicts || []).length > 0 && (
                    <div className="ecm-result-line is-warn">
                      <Warning size={14} />
                      <div>
                        Already used by another client:
                        <ul>
                          {drawerResult.conflicts.map((c) => (
                            <li key={c.value}>
                              <span className="ecm-mono">{c.value}</span> → {c.client_name}
                            </li>
                          ))}
                        </ul>
                        Use <strong>Move</strong> on the All mappings tab to reassign it.
                      </div>
                    </div>
                  )}
                  {(drawerResult.invalid || []).length > 0 && (
                    <div className="ecm-result-line is-bad">
                      <Warning size={14} />
                      <div>
                        <ul>
                          {drawerResult.invalid.map((c) => (
                            <li key={c.value}><span className="ecm-mono">{c.value}</span> — {c.error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="ecm-drawer-foot">
              <button type="button" className="tk-btn" onClick={() => setDrawerOpen(false)}>Done</button>
              <button
                type="button"
                className="tk-btn tk-btn-primary"
                disabled={savingDrawer}
                onClick={submitDrawer}
              >
                {savingDrawer ? 'Adding…' : 'Add mapping'}
              </button>
            </div>
          </aside>
        </>
      )}

      {csvOpen && (
        <>
          <div className="ecm-scrim" role="presentation" onClick={closeCsv} />
          <div className="ecm-modal" role="dialog" aria-label="Import sender mappings">
            <div className="ecm-drawer-head">
              <div>
                <div className="ecm-drawer-title">Import sender mappings</div>
                <div className="ecm-sub">{mailbox?.email_address}</div>
              </div>
              <button type="button" className="ecm-icon-btn" onClick={closeCsv} aria-label="Close">
                <X size={16} />
              </button>
            </div>

            <div className="ecm-modal-body">
              {!csvReport && !csvResult && (
                <div className="ecm-upload">
                  <UploadSimple size={26} />
                  <div className="ecm-upload-title">Choose a CSV or Excel file</div>
                  <p className="ecm-hint">
                    <strong>Kapil Excel:</strong> <code>BRANCH, COMPANY, Email Address</code> (.xlsx) —
                    each row maps that sender to company + branch.
                  </p>
                  <p className="ecm-hint">
                    <strong>Company-level CSV:</strong> <code>email,client_name</code> (aliases: company, company_name).
                    Branch column blank → tickets show company only.
                  </p>
                  <p className="ecm-hint">
                    <strong>Branch-level CSV:</strong> <code>email,client_name,branch</code> — or a branch sheet
                    like <code>BranchName,Branch,email@…</code> / <code>branch,email</code>. For those, pick the
                    company below (Default company) so every branch row attaches to that company.
                  </p>
                  <p className="ecm-hint">
                    Companies not on this product are still imported as written. Leave branch empty for company-only mapping.
                  </p>
                  <div className="ecm-field" style={{ textAlign: 'left', marginTop: 12 }}>
                    <span className="ecm-label">Default company <span className="ecm-muted">(for branch-only sheets)</span></span>
                    <div className="ecm-ss">
                      <SearchableSelect
                        options={clientSel}
                        value={csvDefaultClient}
                        onChange={(v) => setCsvDefaultClient(v || '')}
                        placeholder="— Optional: pick company —"
                        searchPlaceholder="Search companies…"
                        aria-label="Default company for import"
                      />
                    </div>
                    <span className="ecm-hint">
                      Not needed for Excel files that already have a COMPANY column. Required for branch + email
                      sheets with no company column.
                    </span>
                  </div>
                  <div className="ecm-upload-actions">
                    <button
                      type="button"
                      className="tk-btn tk-btn-primary"
                      disabled={csvBusy}
                      onClick={() => csvInputRef.current?.click()}
                    >
                      {csvBusy ? 'Reading…' : 'Select file'}
                    </button>
                    <button
                      type="button"
                      className="tk-btn"
                      disabled={csvBusy}
                      onClick={() => downloadTemplate(false)}
                    >
                      <DownloadSimple size={14} /> Client sheet ({clientCount} clients)
                    </button>
                    {gapCount > 0 && (
                      <button
                        type="button"
                        className="tk-btn"
                        disabled={csvBusy}
                        onClick={() => downloadTemplate(true)}
                      >
                        <DownloadSimple size={14} /> Unmapped only ({gapCount})
                      </button>
                    )}
                  </div>
                  <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    hidden
                    onChange={(e) => validateCsv(e.target.files?.[0])}
                  />
                </div>
              )}

              {csvReport && !csvResult && (
                <>
                  <div className="ecm-counts">
                    {Object.entries(STATUS_META).map(([key, meta]) => (
                      <div key={key} className={`ecm-count-card is-${meta.tone}`}>
                        <span className="ecm-count-value">{csvReport.counts?.[key] ?? 0}</span>
                        <span className="ecm-count-label">{meta.label}</span>
                      </div>
                    ))}
                  </div>

                  {(csvReport.unknown_clients || []).length > 0 && (
                    <div className="ecm-banner is-warn">
                      <Warning size={16} />
                      <div>
                        <strong>{csvReport.unknown_clients.length} company name(s)</strong> are not on{' '}
                        {mailbox?.product_name || 'this product'}&apos;s client list. They will still be mapped
                        as written (company + branch + email):
                        <div className="ecm-unknown">
                          {csvReport.unknown_clients.slice(0, 12).map((u) => (
                            <span key={u.client_name}>{u.client_name} <em>({u.rows})</em></span>
                          ))}
                          {csvReport.unknown_clients.length > 12 && <span>+{csvReport.unknown_clients.length - 12} more</span>}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="ecm-toolbar">
                    <span className="ecm-muted">
                      {csvReport.lines_read ?? csvReport.total} row(s) read · {csvReport.total} address(es) ·{' '}
                      <strong>{csvReport.importable}</strong> will be written
                      {csvReport.counts?.blank ? ` · ${csvReport.counts.blank} client row(s) left blank (ignored)` : ''}
                    </span>
                    <label className="ecm-check ecm-right">
                      <input
                        type="checkbox"
                        checked={csvHideClean}
                        onChange={(e) => setCsvHideClean(e.target.checked)}
                      />
                      Show only problems
                    </label>
                  </div>

                  <div className="ecm-preview">
                    <table className="ecm-table">
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Status</th>
                          <th>Sender</th>
                          <th>Company</th>
                          <th>Branch</th>
                          <th>Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(csvReport.rows || [])
                          .filter((r) => (csvHideClean ? !['new', 'duplicate'].includes(r.status) : true))
                          .slice(0, 500)
                          .map((r, i) => (
                            <tr key={`${r.line}-${i}-${r.input_value}`}>
                              <td className="ecm-muted">{r.line}</td>
                              <td>
                                <span className={`ecm-tag is-${STATUS_META[r.status]?.tone || 'muted'}`}>
                                  {STATUS_META[r.status]?.label || r.status}
                                </span>
                              </td>
                              <td className="ecm-mono">{r.match_value || r.input_value}</td>
                              <td>{r.client_name || r.input_client || '—'}</td>
                              <td>{r.branch || <span className="ecm-muted">—</span>}</td>
                              <td className="ecm-muted">{r.message || ''}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {csvResult && (
                <div className="ecm-result-panel">
                  <div className="ecm-counts">
                    <div className="ecm-count-card is-ok">
                      <span className="ecm-count-value">{csvResult.created}</span>
                      <span className="ecm-count-label">Created</span>
                    </div>
                    <div className="ecm-count-card is-warn">
                      <span className="ecm-count-value">{csvResult.updated}</span>
                      <span className="ecm-count-label">Updated</span>
                    </div>
                    <div className="ecm-count-card is-muted">
                      <span className="ecm-count-value">{csvResult.skipped}</span>
                      <span className="ecm-count-label">Unchanged</span>
                    </div>
                    <div className="ecm-count-card is-bad">
                      <span className="ecm-count-value">{csvResult.rejected}</span>
                      <span className="ecm-count-label">Rejected</span>
                    </div>
                  </div>
                  {csvResult.rejected > 0 && (
                    <p className="ecm-hint">
                      Rejected rows are unchanged in the system. Download them, fix the client names or
                      addresses, and re-upload — importing the same file twice is safe.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="ecm-drawer-foot">
              {csvReport && !csvResult && (
                <button type="button" className="tk-btn" onClick={() => { setCsvReport(null); setCsvFile(null); }}>
                  Choose another file
                </button>
              )}
              {(csvResult?.rejected > 0 || (csvReport && !csvResult && (csvReport.counts?.invalid || csvReport.counts?.unknown_client))) && (
                <button type="button" className="tk-btn" onClick={downloadRejected}>
                  <DownloadSimple size={14} /> Download rejected rows
                </button>
              )}
              {csvResult ? (
                <button type="button" className="tk-btn tk-btn-primary" onClick={closeCsv}>Close</button>
              ) : (
                <button
                  type="button"
                  className="tk-btn tk-btn-primary"
                  disabled={!csvReport || csvBusy || !csvReport.importable}
                  onClick={commitCsv}
                >
                  {csvBusy ? 'Importing…' : `Import ${csvReport?.importable || 0} row(s)`}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </ModuleShell>
  );
}
